import { useCallback, useEffect, useRef, useState } from 'react';
import { Bold, Link2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './Button';

/**
 * Minimal WYSIWYG: bold + hyperlink. Stores HTML in value/onChange.
 * Toolbar is a vertical stack to the left of the editable area.
 */
export function MinimalRichTextEditor({
  value = '',
  onChange,
  placeholder = '',
  disabled = false,
  className,
  editorClassName,
  rows = 6,
  id,
}) {
  const editorRef = useRef(null);
  const [showPlaceholder, setShowPlaceholder] = useState(() => !textFromHtml(value));

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el || !onChange) return;
    const html = el.innerHTML;
    const visible = textFromHtml(html);
    setShowPlaceholder(!visible);
    onChange(visible ? html : '');
  }, [onChange]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const domHtml = el.innerHTML;
    const propVal = value || '';
    if (propVal === domHtml) return;
    if (document.activeElement === el) return;
    el.innerHTML = propVal;
    setShowPlaceholder(!textFromHtml(propVal));
  }, [value]);

  const runCommand = (fn) => {
    if (disabled) return;
    editorRef.current?.focus();
    fn();
    emitChange();
  };

  const handleBold = () => {
    runCommand(() => {
      document.execCommand('bold');
    });
  };

  const handleLink = () => {
    if (disabled) return;
    editorRef.current?.focus();
    let url = window.prompt('Link URL (https://, mailto:, or tel:)', 'https://');
    if (url == null) return;
    url = url.trim();
    if (!url) return;
    if (
      !/^https?:\/\//i.test(url) &&
      !/^mailto:/i.test(url) &&
      !/^tel:/i.test(url)
    ) {
      url = `https://${url}`;
    }
    document.execCommand('createLink', false, url);
    emitChange();
  };

  const minHeightRem = Math.max(3, rows) * 1.125;

  return (
    <div className={cn('flex w-full gap-2', className)}>
      <div className="flex flex-col gap-1 shrink-0 pt-0.5">
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleBold}
          title="Bold"
          aria-label="Bold"
        >
          <Bold className="h-3.5 w-3.5" strokeWidth={2.25} />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleLink}
          title="Insert link"
          aria-label="Insert link"
        >
          <Link2 className="h-3.5 w-3.5" strokeWidth={2.25} />
        </Button>
      </div>
      <div className="relative flex-1 min-w-0">
        {showPlaceholder && placeholder ? (
          <span
            className="pointer-events-none absolute left-3 top-2 z-0 text-meta text-[var(--ds-text-muted)] select-none"
            aria-hidden
          >
            {placeholder}
          </span>
        ) : null}
        <div
          id={id}
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-placeholder={placeholder || undefined}
          contentEditable={!disabled}
          suppressContentEditableWarning
          className={cn(
            'relative z-[1] min-h-[80px] w-full overflow-y-auto',
            'bg-[var(--ds-control-field-bg)]',
            'border border-[var(--ds-control-field-border)]',
            'rounded-[var(--ds-radius-md)]',
            'px-3 py-2',
            'text-meta text-[var(--ds-text-primary)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)]',
            'focus:border-[var(--ds-accent-primary)]',
            'transition-colors duration-180',
            disabled && 'cursor-not-allowed opacity-50',
            '[&_a]:text-[var(--ds-accent-primary)] [&_a]:underline',
            editorClassName
          )}
          style={{ minHeight: `${minHeightRem}rem` }}
          onInput={emitChange}
          onBlur={emitChange}
        />
      </div>
    </div>
  );
}

function textFromHtml(html) {
  if (!html || typeof html !== 'string') return false;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return Boolean(tmp.textContent?.trim());
}

MinimalRichTextEditor.displayName = 'MinimalRichTextEditor';
